import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { API } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, FileText, MessageCircle, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import useRevalidate from "@/lib/useRevalidate";

export function BillList() {
  const { t } = useApp();
  const [bills, setBills] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const customerFilter = params.get("customer_id");
  const preselect = params.get("preselect") === "1";

  const load = () => api.get("/bills").then((r) => setBills(r.data));
  useEffect(() => { load(); }, []);
  useRevalidate(load);

  const visibleBills = useMemo(
    () => customerFilter ? bills.filter((b) => b.customer_id === customerFilter) : bills,
    [bills, customerFilter],
  );
  const unpaidBills = useMemo(() => visibleBills.filter((b) => b.status !== "paid" && (b.total - b.amount_paid) > 0), [visibleBills]);

  // Auto-preselect on deep-link (once, when bills first load with a customer filter)
  useEffect(() => {
    if (preselect && customerFilter && unpaidBills.length > 0 && selected.size === 0) {
      setSelected(new Set(unpaidBills.map((b) => b.id)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselect, customerFilter, unpaidBills.length]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectedTotal = useMemo(() => {
    return unpaidBills.filter((b) => selected.has(b.id)).reduce((s, b) => s + (b.total - b.amount_paid), 0);
  }, [selected, unpaidBills]);
  const selectAll = () => setSelected(new Set(unpaidBills.map((b) => b.id)));
  const clear = () => setSelected(new Set());
  const clearFilter = () => { setSelected(new Set()); setParams({}); };

  const finalize = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bills/bulk-finalize", { bill_ids: Array.from(selected) });
      toast.success(`${r.data.finalized} bills marked paid · ${r.data.skipped} skipped`);
      clear(); setConfirmOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const filterName = customerFilter && visibleBills[0]?.customer_name;

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-3xl font-bold text-stone-900">{t("bills")}</h1>
        <Link to="/bills/new" data-testid="bills-new-btn" className="h-12 px-5 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> {t("new_bill")}
        </Link>
      </div>
      {customerFilter && (
        <div className="p-3 rounded-xl bg-[#FFF7ED] border border-[#B45309]/30 flex items-center justify-between gap-2 flex-wrap" data-testid="bills-customer-filter-banner">
          <div className="text-xs sm:text-sm text-stone-700">
            <span className="font-semibold text-[#B45309]">{filterName || t("customer")}</span> · {t("showing_pending_bills").replace("{n}", unpaidBills.length)}
          </div>
          <button onClick={clearFilter} data-testid="bills-clear-filter" className="text-xs font-semibold text-[#B45309] hover:underline">{t("show_all_bills")}</button>
        </div>
      )}
      {bills.length === 0 && <Card className="p-10 text-center text-stone-500" data-testid="empty-bills">{t("empty_bills")}</Card>}
      {unpaidBills.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="bulk-finalize-bar">
          <span className="text-stone-500 font-mono">{unpaidBills.length} unpaid</span>
          <button onClick={selectAll} data-testid="bills-select-all" className="h-8 px-3 rounded-full bg-stone-100 text-stone-700 font-semibold">{t("select_all")}</button>
          {selected.size > 0 && (
            <>
              <button onClick={clear} data-testid="bills-clear-selection" className="h-8 px-3 rounded-full bg-stone-100 text-stone-700 font-semibold">{t("clear")}</button>
              <span className="ml-auto font-mono text-stone-600" data-testid="bills-selected-total">{selected.size} · ₹{selectedTotal.toFixed(2)}</span>
              <button onClick={() => setConfirmOpen(true)} data-testid="bills-bulk-finalize-btn"
                      className="h-9 px-4 rounded-full bg-[#166534] hover:bg-[#14532D] text-white font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> {t("mark_paid")}
              </button>
            </>
          )}
        </div>
      )}
      <div className="space-y-2">
        {visibleBills.map((b) => {
          const isUnpaid = b.status !== "paid" && (b.total - b.amount_paid) > 0;
          const isSelected = selected.has(b.id);
          return (
            <div key={b.id} data-testid={`bill-row-${b.id}`}
                 className={`flex items-center gap-2 p-4 rounded-2xl border transition-colors ${isSelected ? "border-[#166534] bg-green-50/60" : "border-stone-200 bg-white hover:bg-stone-50"}`}>
              {isUnpaid && (
                <input type="checkbox" checked={isSelected} onChange={() => toggle(b.id)} data-testid={`bill-select-${b.id}`}
                       className="h-5 w-5 shrink-0 accent-[#166534] cursor-pointer" onClick={(e) => e.stopPropagation()} />
              )}
              <Link to={`/bills/${b.id}`} className="flex-1 min-w-0 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-stone-900">{b.bill_no}</div>
                  <div className="text-sm text-stone-600 truncate">{b.customer_name || "—"}</div>
                  <div className="text-xs text-stone-500">{b.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold text-lg text-stone-900">₹{b.total.toFixed(2)}</div>
                  <div className={`text-xs font-medium ${b.status === "paid" ? "text-[#166534]" : b.status === "credit" ? "text-red-600" : "text-amber-600"}`}>{b.status}</div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("mark_paid")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("mark_paid_confirm").replace("{n}", selected.size).replace("{amount}", selectedTotal.toFixed(2))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-fin-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={finalize} disabled={busy} data-testid="bulk-fin-confirm" className="bg-[#166534] hover:bg-[#14532D]">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {t("mark_paid")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function BillView() {
  const { t, lang } = useApp();
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [returns, setReturns] = useState([]);
  const [retOpen, setRetOpen] = useState(false);
  const [retLines, setRetLines] = useState([]);
  const [retNotes, setRetNotes] = useState("");
  const [refundAmount, setRefundAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/bills/${id}`).then((r) => setB(r.data));
    api.get(`/bills/${id}/returns`).then((r) => setReturns(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (b?.customer_id) api.get(`/customers`).then((cr) => setCustomer(cr.data.find((c) => c.id === b.customer_id)));
  }, [b]);

  if (!b) return null;

  // Compute remaining qty per line (original - sum of returned so far)
  const remainingByKey = {};
  for (const l of b.lines) remainingByKey[l.item_id || l.name_en] = Number(l.quantity);
  for (const r of returns) for (const rl of r.lines) {
    const k = rl.item_id || rl.name_en;
    if (k in remainingByKey) remainingByKey[k] -= Number(rl.quantity);
  }
  const anyReturnable = Object.values(remainingByKey).some((v) => v > 0);

  const openReturn = () => {
    const initial = b.lines.map((l) => {
      const key = l.item_id || l.name_en;
      const remaining = remainingByKey[key] || 0;
      return { key, item_id: l.item_id, name_en: l.name_en, name_gu: l.name_gu, unit: l.unit,
               rate: l.rate, remaining, quantity: 0 };
    });
    setRetLines(initial); setRetNotes(""); setRefundAmount(0); setRetOpen(true);
  };

  const setRetLine = (i, q) => setRetLines((prev) => prev.map((x, idx) => idx === i ? { ...x, quantity: q } : x));
  const retTotal = retLines.reduce((s, r) => s + (Number(r.quantity) || 0) * Number(r.rate), 0);

  const submitReturn = async () => {
    const activeLines = retLines.filter((r) => (Number(r.quantity) || 0) > 0);
    if (activeLines.length === 0) return toast.error("Add at least one line");
    for (const r of activeLines) {
      if (r.quantity > r.remaining + 1e-6) return toast.error(t("over_return"));
    }
    setBusy(true);
    try {
      const body = {
        lines: activeLines.map((r) => ({
          item_id: r.item_id || null, name_en: r.name_en, name_gu: r.name_gu || "",
          unit: r.unit, quantity: Number(r.quantity), rate: Number(r.rate),
          total: Number(r.quantity) * Number(r.rate),
        })),
        refund_amount: Math.min(Number(refundAmount) || 0, retTotal),
        notes: retNotes, language: lang,
      };
      const res = await api.post(`/bills/${id}/return`, body);
      toast.success(t("return_created"));
      setRetOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const nudgeOnWhatsApp = () => {
    const phone = (customer?.phone || "").replace(/\D/g, "");
    const balance = (b.total - b.amount_paid).toFixed(2);
    const templ = lang === "gu"
      ? `નમસ્તે ${b.customer_name || ""}, બિલ ${b.bill_no} નું ₹${balance} બાકી છે. અનુકૂળતાએ ચૂકવવા વિનંતી. આભાર 🙏`
      : `Hi ${b.customer_name || ""}, a gentle reminder — bill ${b.bill_no} has ₹${balance} pending. Please pay at your convenience. Thank you 🙏`;
    const url = phone
      ? `https://wa.me/${phone.length === 10 ? "91" + phone : phone}?text=${encodeURIComponent(templ)}`
      : `https://wa.me/?text=${encodeURIComponent(templ)}`;
    window.open(url, "_blank");
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post("/bills/bulk-finalize", { bill_ids: [id] });
      toast.success(t("bill_marked_paid"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const token = localStorage.getItem("token");
  const pdfUrl = `${API}/bills/${id}/pdf`;

  const fetchPdfBlob = async () => {
    const res = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
    return res.blob();
  };

  const viewPdf = async (e) => {
    e.preventDefault();
    const blob = await fetchPdfBlob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const shareWhatsApp = async () => {
    const templ = t("whatsapp_summary");
    const msg = templ
      .replace("{name}", b.customer_name || "")
      .replace("{no}", b.bill_no)
      .replace("{total}", b.total.toFixed(2));
    try {
      if (navigator.canShare && navigator.share) {
        const blob = await fetchPdfBlob();
        const file = new File([blob], `bill-${b.bill_no}.pdf`, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: msg, title: `Bill ${b.bill_no}` });
          return;
        }
      }
    } catch (err) { /* fall through */ }
    const phone = (customer?.phone || "").replace(/\D/g, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    toast.info("Attach the PDF in WhatsApp after the chat opens");
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("bills")}</div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 font-mono">{b.bill_no}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {b.status !== "paid" && (b.total - b.amount_paid) > 0 && (
            <>
              <button onClick={markPaid} disabled={busy} data-testid="bill-mark-paid"
                      className="h-12 px-5 rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4" /> {t("mark_paid")} · ₹{(b.total - b.amount_paid).toFixed(2)}
              </button>
              <button onClick={nudgeOnWhatsApp} data-testid="bill-nudge-wa"
                      className="h-12 px-4 rounded-xl bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold flex items-center gap-2 text-sm">
                <MessageCircle className="w-4 h-4" /> {t("nudge_wa")}
              </button>
            </>
          )}
          {anyReturnable && (
            <button onClick={openReturn} data-testid="bill-return-btn"
                    className="h-12 px-4 rounded-xl border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 font-semibold flex items-center gap-2 text-sm transition-colors">
              <RotateCcw className="w-4 h-4" /> {t("record_return")}
            </button>
          )}
          <button onClick={shareWhatsApp} data-testid="bill-whatsapp"
                  className="h-12 px-5 rounded-xl bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold flex items-center gap-2 text-sm transition-colors">
            <MessageCircle className="w-4 h-4" /> {t("share_whatsapp")}
          </button>
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="bill-view-pdf" onClick={viewPdf}
             className="h-12 px-5 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4" /> {t("view_pdf")}
          </a>
        </div>
      </div>
      <Card className="p-4 sm:p-6 rounded-2xl">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("customer")}</div><div className="mt-1 font-medium">{b.customer_name || "—"}</div></div>
          <div className="text-right"><div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("status")}</div><div className="mt-1 font-medium">{b.status}</div></div>
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0 mt-6">
          <table className="w-full text-sm min-w-[420px] px-4 sm:px-0">
            <thead className="text-xs uppercase tracking-widest text-stone-500">
              <tr><th className="text-left py-2 pl-4 sm:pl-0">{t("items")}</th><th className="text-right py-2">{t("quantity")}</th><th className="text-right py-2">{t("rate")}</th><th className="text-right py-2 pr-4 sm:pr-0">{t("amount")}</th></tr>
            </thead>
            <tbody>
              {b.lines.map((l, i) => (
                <tr key={i} className="border-t border-stone-100">
                  <td className="py-2 pl-4 sm:pl-0"><div className="font-medium">{l.name_en}</div><div className="text-xs text-stone-500 font-gujarati">{l.name_gu}</div></td>
                  <td className="py-2 text-right font-mono whitespace-nowrap">{l.quantity} {l.unit}</td>
                  <td className="py-2 text-right font-mono whitespace-nowrap">₹{l.rate.toFixed(2)}</td>
                  <td className="py-2 text-right font-mono pr-4 sm:pr-0 whitespace-nowrap">₹{l.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 space-y-1 text-right font-mono">
          <div className="text-sm text-stone-600">{t("subtotal")}: ₹{b.subtotal.toFixed(2)}</div>
          <div className="text-sm text-stone-600">{t("discount")}: ₹{b.discount.toFixed(2)}</div>
          <div className="text-xl sm:text-2xl font-bold text-stone-900">{t("total")}: ₹{b.total.toFixed(2)}</div>
          <div className="text-sm text-stone-600">{t("paid")}: ₹{b.amount_paid.toFixed(2)}</div>
        </div>
      </Card>

      {returns.length > 0 && (
        <Card className="p-4 rounded-2xl border-red-200 bg-red-50/40">
          <div className="text-xs font-bold uppercase tracking-widest text-red-700 mb-2 flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> {t("returns")} ({returns.length})
          </div>
          <div className="space-y-1" data-testid="bill-returns">
            {returns.map((r) => (
              <Link key={r.id} to={`/returns/${r.id}`} data-testid={`bill-return-link-${r.id}`}
                    className="flex justify-between p-2 rounded-lg hover:bg-red-100/50 text-sm">
                <span className="font-mono font-medium">{r.return_no}</span>
                <span className="font-mono text-red-700">−₹{r.total.toFixed(2)}</span>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={retOpen} onOpenChange={setRetOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("record_return")}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {retLines.map((rl, i) => (
              <div key={i} data-testid={`return-line-${i}`} className={`p-3 rounded-lg border ${rl.remaining <= 0 ? "opacity-50 bg-stone-50" : "border-stone-200"}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{rl.name_en}</div>
                    <div className="text-xs text-stone-500 font-gujarati truncate">{rl.name_gu}</div>
                  </div>
                  <div className="text-xs text-stone-500 shrink-0">{t("max_qty")}: <span className="font-mono font-semibold">{rl.remaining} {rl.unit}</span></div>
                </div>
                <div className="flex gap-2 items-center">
                  <Input type="number" min={0} max={rl.remaining} step="any"
                         value={rl.quantity || ""} onChange={(e) => setRetLine(i, parseFloat(e.target.value) || 0)}
                         disabled={rl.remaining <= 0}
                         data-testid={`return-qty-${i}`} className="h-11 rounded-lg font-mono w-28" />
                  <span className="text-sm text-stone-500">× ₹{rl.rate.toFixed(2)}</span>
                  <span className="ml-auto font-mono font-semibold">₹{((rl.quantity || 0) * rl.rate).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-stone-100">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("return_total")}</label>
              <div className="h-12 rounded-lg bg-stone-100 grid place-items-center font-mono text-lg font-bold text-red-600" data-testid="return-total">−₹{retTotal.toFixed(2)}</div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("refund_amount")}</label>
              <Input type="number" min={0} max={retTotal} value={refundAmount} onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)} data-testid="return-refund" className="h-12 rounded-lg font-mono" />
            </div>
          </div>
          <Input placeholder={t("return_notes")} value={retNotes} onChange={(e) => setRetNotes(e.target.value)} data-testid="return-notes" className="h-12 rounded-lg" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setRetOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submitReturn} disabled={busy || retTotal <= 0} data-testid="return-submit"
                    className="bg-red-600 hover:bg-red-700 text-white">
              <RotateCcw className="w-4 h-4 mr-2" /> {t("submit_return")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
